import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaEyeSlash, FaEye } from "react-icons/fa";
import { AuthApis } from "../../../apis/authApis";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValidated, setTokenValidated] = useState(false);
  const [tokenIsValid, setTokenIsValid] = useState(false);
  const [validating, setValidating] = useState(true);
  const [passwordError, setPasswordError] = useState("");
  
  const navigate = useNavigate();

  // Validate token on component mount
  useEffect(() => {
    if (!token || !email) {
      toast.error("Invalid reset link. Please request a new one.");
      setValidating(false);
      return;
    }

    validateToken();
  }, []);

  const validateToken = async () => {
    try {
      const response = await AuthApis.verifyResetToken({ token, email });
      if (response?.data?.status === 'success' && response?.data?.valid) {
        setTokenIsValid(true);
      } else {
        toast.error("This password reset link has expired or is invalid.");
      }
    } catch (error: any) {
      console.error("Token validation error:", error);
      toast.error(error?.response?.data?.message || "Invalid reset link");
    } finally {
      setValidating(false);
      setTokenValidated(true);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    validatePassword(newPassword, confirmPassword);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    validatePassword(password, newConfirmPassword);
  };

  const validatePassword = (pass: string, confirmPass: string) => {
    // Password strength validation
    if (pass.length > 0) {
      const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[a-zA-Z\d!@#$%^&*()_+]{8,}$/;
      
      if (!strongPasswordRegex.test(pass)) {
        setPasswordError('Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.');
        return false;
      }
      
      // Password match validation if both fields have values
      if (confirmPass.length > 0 && pass !== confirmPass) {
        setPasswordError("Passwords don't match");
        return false;
      }
    }
    
    setPasswordError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords again before submission
    if (!validatePassword(password, confirmPassword)) {
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);

    try {
      const response = await AuthApis.resetPassword({
        token,
        email,
        password,
        password_confirmation: confirmPassword
      });

      if (response?.data?.status === 'success') {
        toast.success(response.data.message || "Password reset successful");
        
        // Redirect to login after a delay
        setTimeout(() => {
          navigate('/sign-in');
        }, 2000);
      } else {
        toast.error(response?.data?.message || "Failed to reset password");
      }
    } catch (error: any) {
      console.error("Reset password error:", error);
      toast.error(error?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while validating token
  if (validating) {
    return (
      <section className="bg-[#F8F8F8] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying your reset link...</p>
        </div>
      </section>
    );
  }

  // Show error if token is invalid
  if (tokenValidated && !tokenIsValid) {
    return (
      <section className="bg-[#F8F8F8] min-h-screen body-font font-poppins flex items-center justify-center">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-lg text-center">
          <div className="text-red-500 mb-4">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-12 w-12 mx-auto" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-3">Invalid or Expired Link</h2>
          <p className="text-gray-600 mb-5">
            This password reset link has expired or is invalid. Please request a new one.
          </p>
          <a
            href="/forgot-password"
            className="block w-full bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Request New Reset Link
          </a>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="bg-[#F8F8F8] min-h-screen body-font font-poppins">
        <div className="bg-gray-100 py-10 pl-10">
          <img src="images/log.svg" alt="Logo" />
        </div>
        <div className="flex flex-col md:flex-row">
          {/* Left Section - Images */}
          <div className="bg-gray-100 flex-1 hidden md:flex flex-col items-center justify-center p-4">
            <div className="grid grid-cols-2 gap-4 w-full max-w-md">
              <img
                src="images/img4.png"
                alt="Family 1"
                className="rounded-lg object-cover w-full h-full"
              />
              <img
                src="images/img5.png"
                alt="Family 2"
                className="rounded-lg object-cover w-full h-full"
              />
             <img
                src="images/img11.png"
                alt="Family 3"
                className="rounded-lg object-cover w-full h-full"
              />
              <img
                src="images/img2.png"
                alt="Family 4"
                className="rounded-lg object-cover w-full h-full"
              />
            </div>
          </div>

          {/* Right Section - Form */}
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            <div className="max-w-md w-full">
              <h1 className="text-2xl font-bold mb-2 text-center">Create New Password</h1>
              <p className="text-gray-600 mb-6 text-center">
                Your new password must be different from previously used passwords.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="New Password"
                    value={password}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-3 pr-10 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <button 
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm New Password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    className="w-full px-4 py-3 pr-10 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <button 
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                
                {passwordError && (
                  <p className="text-red-500 text-sm">{passwordError}</p>
                )}
                
                <button
                  type="submit"
                  disabled={loading || !!passwordError}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                    loading || passwordError 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-orange-500 hover:bg-orange-600 text-white'
                  }`}
                >
                  {loading ? 'Resetting Password...' : 'Reset Password'}
                </button>
              </form>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  Remember your password? <a href="/sign-in" className="text-blue-500 hover:underline">Sign in</a>
                </p>
              </div>
              
              <div className="mt-8">
                <p className="text-center text-sm text-gray-500">
                  &copy; 2023 bETERNAL. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ToastContainer
        position="bottom-left"
        autoClose={2000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}

export default ResetPassword;